import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;
  #queuedTaps = 0;
  #queuedForm;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') {
        // Button stays tappable: count extra taps, fly each one now, and add them once the current add finishes.
        this.#queuedTaps += 1;
        this.#bumpCartCount(this.#tapQuantity(event.target));
        this.#queuedForm = event.target;
        this.#flyToCart();
        return;
      }

      this.#bumpCartCount(this.#tapQuantity(event.target));
      this.#submit(event.target, event.submitter);
    }, opts);

    // Compact cards pick the variant with a plain <select data-variant-select>; mirror it
    // into the form's hidden id input so the add-to-cart sends the chosen option.
    this.addEventListener('change', (event) => {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.matches('[data-variant-select]')) return;
      const input = select.form ? this.#variantInput(select.form) : null;
      if (input) input.value = select.value;
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter, { multiplier = 1, fly = true } = {}) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (line) line.quantity *= multiplier;
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');
    // Start the fly-to-cart right away so it runs while the cart request is in flight.
    const flight = fly ? this.#flyToCart() : null;

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
        this.#bumpCartCount(-line.quantity);
      } else {
        // Stay on the product; the photo is already flying into the cart icon (started on click).
        void flight;
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#bumpCartCount(-line.quantity);
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
        if (this.#queuedTaps > 0 && this.#queuedForm) {
          const taps = this.#queuedTaps;
          const queuedForm = this.#queuedForm;
          this.#queuedTaps = 0;
          this.#queuedForm = null;
          // The refresh just showed the server count; re-add the queued taps on top until they are saved.
          this.#bumpCartCount(taps * this.#tapQuantity(queuedForm));
          this.#submit(queuedForm, null, { multiplier: taps, fly: false });
        }
      }
    }
  }

  /**
   * Flies a copy of the visible product photo into the visible cart icon
   * (bottom bar on mobile, header on desktop), then gives the icon a little bump.
   * Skipped for shoppers who prefer reduced motion.
   */
  async #flyToCart() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const isVisible = (el) => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const targets = [...document.querySelectorAll('[data-fly-cart-target]')].filter(isVisible);
    const target = targets.find((el) => el.hasAttribute('data-fly-cart-mobile')) ?? targets[0];

    // Fly this form's own photo when it has one (collection featured cards), else the page gallery.
    const ownImages = [...this.querySelectorAll('[data-product-gallery] img')];
    const images = ownImages.length > 0 ? ownImages : [...document.querySelectorAll('[data-product-gallery] img')];
    const inView = (img) => {
      const rect = img.getBoundingClientRect();
      return rect.width > 0 && rect.right > 0 && rect.left < window.innerWidth && rect.bottom > 0 && rect.top < window.innerHeight;
    };
    const source = images.find(inView) ?? images.find(isVisible);
    if (!target || !source) return;

    const from = source.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const size = Math.min(from.width, from.height, 220);
    const startX = from.left + from.width / 2 - size / 2;
    const startY = from.top + from.height / 2 - size / 2;
    const endX = to.left + to.width / 2 - size / 2;
    const endY = to.top + to.height / 2 - size / 2;
    const dx = endX - startX;
    const dy = endY - startY;
    const scale = Math.max(to.width / size, 0.08);

    const flyer = source.cloneNode(false);
    // Reuse the exact image already on screen (no new download) so the flight shows instantly.
    if (source.currentSrc) flyer.src = source.currentSrc;
    flyer.removeAttribute('srcset');
    flyer.removeAttribute('sizes');
    flyer.decoding = 'sync';
    flyer.removeAttribute('loading');
    flyer.removeAttribute('fetchpriority');
    flyer.removeAttribute('id');
    flyer.setAttribute('aria-hidden', 'true');
    flyer.alt = '';
    Object.assign(flyer.style, {
      position: 'fixed',
      left: `${startX}px`,
      top: `${startY}px`,
      width: `${size}px`,
      height: `${size}px`,
      objectFit: 'cover',
      borderRadius: '12px',
      boxShadow: '0 12px 30px rgb(0 0 0 / 0.25)',
      zIndex: '2147483000',
      pointerEvents: 'none',
      margin: '0',
      transform: 'none',
    });
    document.body.append(flyer);

    const lift = Math.min(120, Math.abs(dy) * 0.3 + 40);
    const flight = flyer.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1, borderRadius: '12px' },
        { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - lift}px) scale(0.55)`, opacity: 1, offset: 0.5 },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0.6, borderRadius: '50%' },
      ],
      { duration: 500, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
    );

    try {
      await flight.finished;
    } catch {
      // Animation interrupted; still clean up below.
    }
    flyer.remove();

    target.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }],
      { duration: 250, easing: 'ease-out' }
    );
  }

  #tapQuantity(form) {
    const value = Number.parseInt(String(new FormData(form).get("quantity") || "1"), 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * Updates the header and bottom-bar cart badges right away (optimistic).
   * The cart partial refresh then replaces them with the real count from the server.
   */
  #bumpCartCount(delta) {
    for (const badge of document.querySelectorAll("[data-cart-count-badge]")) {
      const current = Number.parseInt(badge.textContent.trim(), 10) || 0;
      const next = Math.max(0, current + delta);
      badge.textContent = String(next);
      badge.classList.toggle("hidden", next === 0);
      badge.classList.toggle("flex", next > 0);
    }
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        // Keep the button enabled-looking; extra taps are queued in the submit listener.
        // No dimming: the button stays bright so every tap feels instant.
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
